import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';

const SPEED_STEPS = [1, 1.5, 2];

const formatDuration = (seconds = 0) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${mins}:${secs}`;
};

export default function AudioPlayer({ url }) {
  const audioRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [speedIndex, setSpeedIndex] = useState(0);

  const playbackRate = SPEED_STEPS[speedIndex] ?? 1;
  const formattedCurrent = useMemo(() => formatDuration(currentTime), [currentTime]);
  const formattedDuration = useMemo(() => formatDuration(duration), [duration]);
  const formattedTimeLabel = useMemo(
    () => (isPlaying ? `${formattedCurrent} / ${formattedDuration}` : formattedDuration),
    [formattedCurrent, formattedDuration, isPlaying]
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedData = () => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
      setIsReady(true);
    };
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('loadedmetadata', handleLoadedData);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedData);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setIsPlaying(false);
    setIsReady(false);
    setCurrentTime(0);
    audio.pause();
    audio.currentTime = 0;
    audio.load();
  }, [url]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = playbackRate;
  }, [playbackRate]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        await audio.play();
        setIsPlaying(true);
      }
    } catch (err) {
      console.warn('Não foi possível reproduzir o áudio:', err);
    }
  };

  const handleSeek = (event) => {
    const audio = audioRef.current;
    if (!audio || !isReady) return;
    const nextTime = Number(event.target.value);
    if (!Number.isFinite(nextTime)) return;
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const cycleSpeed = () => {
    const nextIndex = (speedIndex + 1) % SPEED_STEPS.length;
    setSpeedIndex(nextIndex);
  };

  return (
    <div className="audio-wrapper">
      <button
        type="button"
        className="audio-control-btn"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pausar áudio' : 'Reproduzir áudio'}
      >
        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
      </button>
      <div className="audio-progress">
        <input
          type="range"
          min={0}
          max={duration || 0}
          value={Math.min(currentTime, duration || 0)}
          onChange={handleSeek}
          disabled={!isReady}
        />
        <span className="audio-time">{formattedTimeLabel}</span>
      </div>
      <button
        type="button"
        className="audio-speed-btn"
        onClick={cycleSpeed}
        aria-label={`Velocidade ${playbackRate}x`}
        disabled={!isReady}
      >
        {`${playbackRate}x`}
      </button>
      <audio ref={audioRef} preload="metadata" style={{ display: 'none' }}>
        <source src={url} type="audio/webm" />
        <source src={url.replace('.webm', '.mp3')} type="audio/mp3" />
        Seu navegador não suporta o player de áudio.
      </audio>
    </div>
  );
}
